/**
 * Login Reducer
 *
 * Manages authentication state. The loginResponse object contains
 * the decoded user data including JWT token, user details, and permissions.
 * This state is persisted to localStorage via redux-persist (whitelisted in root-reducer).
 *
 * SECURITY NOTE: The entire loginResponse (including JWT token) is stored
 * in localStorage. Consider using sessionStorage for better security,
 * or implement token refresh and expiration handling.
 */
import LoginTypes from './login.types';
import { decodeHexResponse } from '../_common/common.utils';

const INITIAL_STATE = {
	loginResponse: null,
};

const loginReducer = (state = INITIAL_STATE, action: { type: string; payload?: any }) => {
	switch (action.type) {
		// Reset state when login is initiated
		case LoginTypes.LOGIN_START:
			return { ...state, loginResponse: null };
		// Decrypt and store the successful login response
		case LoginTypes.LOGIN_SUCCESS:
			return {
				...state,
				loginResponse: decodeHexResponse(action.payload, LoginTypes.LOGIN_KEY),
			};
		// Decrypt and store error response for UI display
		case LoginTypes.LOGIN_FAILURE:
			return {
				...state,
				loginResponse: decodeHexResponse(action.payload, LoginTypes.LOGIN_KEY),
			};
		// Clear login response (used after handling the response in components)
		case LoginTypes.LOGIN_RESPONSE_CHANGED:
			return { ...state, loginResponse: null };
		// Reset login state (logout or session cleanup)
		case LoginTypes.LOGIN_RESPONSE_REST_START:
			return { ...state, loginResponse: null };
		// Persist login data directly (used to restore session from storage)
		case LoginTypes.PERSIST_LOGIN_DATA_START:
			return { ...state, loginResponse: action.payload };
		/**
		 * SECURITY FIX: Proper logout action that clears Redux state AND
		 * removes persisted login data from localStorage to prevent stale tokens.
		 * redux-persist will automatically sync this null state to localStorage.
		 */
		case LoginTypes.LOGOUT:
			// Clear any manually stored items from localStorage
			try {
				localStorage.removeItem('persist:login');
				localStorage.removeItem('persist:root');
			} catch (e) {
				if (process.env.NODE_ENV === 'development') console.error('Error clearing localStorage on logout:', e);
			}
			return { ...INITIAL_STATE };
		default:
			return state;
	}
};

export default loginReducer;
